import React, { useEffect, useMemo, useState } from "react";
import { ref, get, child } from "firebase/database";
import { dbAsistencia } from "../firebase";
import "../styles/modalAgregarUsuario.css";

const TYPE_LABELS = {
  op: "No Pude Picar",
  va: "Vacaciones",
  sc: "Comisión de servicio",
  pe: "Permiso ocasional",
  pa: "Personal",
};

function emptyForm() {
  return {
    dni: "",
    nombres: "",
    apePaterno: "",
    apeMaterno: "",
    email: "",
    password: "",
    phone: "",
    address: "",
    managerKey: "",
    designation: "",
    allotted_office: [],
    leaves: { op: 0, va: 0, sc: 0, pe: 0, pa: 0 },
  };
}

function splitNameParts(fullName) {
  const parts = String(fullName || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 1) return { nombres: parts[0] || "", apePaterno: "", apeMaterno: "" };
  if (parts.length === 2) return { nombres: parts[0], apePaterno: parts[1], apeMaterno: "" };
  return {
    nombres: parts.slice(0, parts.length - 2).join(" "),
    apePaterno: parts[parts.length - 2],
    apeMaterno: parts[parts.length - 1],
  };
}

function buildFormFromInitialData(initialData) {
  const base = emptyForm();
  if (!initialData) return base;

  const nameParts = splitNameParts(initialData.name || initialData.Name);
  const officesRaw = String(initialData.allotted_office || "").trim();
  const officeList = officesRaw
    ? officesRaw
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean)
    : [];

  return {
    ...base,
    dni: String(initialData.dni || initialData.UUID || ""),
    nombres: nameParts.nombres,
    apePaterno: nameParts.apePaterno,
    apeMaterno: nameParts.apeMaterno,
    email: String(initialData.email || initialData.Email || ""),
    phone: String(initialData.phone || initialData.PhoneNumber || ""),
    address: String(initialData.address || initialData.Address || ""),
    managerKey: String(initialData.managerCode || initialData.manager || ""),
    designation: String(initialData.designation || ""),
    allotted_office: officeList,
    leaves: {
      op: Number(initialData?.leaves?.op ?? 0),
      va: Number(initialData?.leaves?.va ?? 0),
      sc: Number(initialData?.leaves?.sc ?? 0),
      pe: Number(initialData?.leaves?.pe ?? 0),
      pa: Number(initialData?.leaves?.pa ?? 0),
    },
  };
}

export default function ModalAgregarUsuario({
  open = false,
  mode = "create",
  initialData = null,
  submitting = false,
  onClose,
  onConfirm,
}) {
  const isEditMode = mode === "edit";
  const [form, setForm] = useState(emptyForm());

  const [loadingManagers, setLoadingManagers] = useState(false);
  const [loadingOffices, setLoadingOffices] = useState(false);
  const [managersMap, setManagersMap] = useState({});
  const [officesMap, setOfficesMap] = useState({});
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setForm(buildFormFromInitialData(initialData));
      setError("");
    }
  }, [open, initialData]);

  useEffect(() => {
    if (!open) return;
    let mounted = true;

    (async () => {
      setLoadingManagers(true);
      setLoadingOffices(true);
      setError("");

      const root = ref(dbAsistencia);

      try {
        const snapManagers = await get(child(root, "managers"));
        if (mounted && snapManagers.exists()) {
          const data = snapManagers.val() || {};
          setManagersMap(data);

          const keys = Object.keys(data);
          if (keys.length) {
            const firstKey = keys[0];
            const firstDesig = (data[firstKey]?.designation || "")
              .replace(/^Encargad[ao]\s+de\s*/i, "")
              .trim();

            setForm((f) => ({
              ...f,
              managerKey: f.managerKey || firstKey,
              designation: f.designation || firstDesig,
            }));
          }
        }
      } catch {
        if (mounted) setError("No se pudo cargar la lista de encargados.");
      } finally {
        if (mounted) setLoadingManagers(false);
      }

      try {
        const snapOffices = await get(child(root, "location"));
        if (mounted && snapOffices.exists()) {
          const data = snapOffices.val() || {};
          setOfficesMap(data);

          const keys = Object.keys(data);
          if (keys.length) {
            setForm((f) => ({
              ...f,
              allotted_office:
                Array.isArray(f.allotted_office) && f.allotted_office.length
                  ? f.allotted_office
                  : [keys[0]],
            }));
          }
        }
      } catch {
        if (mounted) setError((prev) => prev || "No se pudo cargar las oficinas.");
      } finally {
        if (mounted) setLoadingOffices(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [open]);

  const areaOptions = useMemo(() => {
    return Object.entries(managersMap).map(([key, val]) => ({
      key,
      designation: val?.designation || "",
      name: val?.name || "",
    }));
  }, [managersMap]);

  const officeOptions = useMemo(() => {
    return Object.entries(officesMap).map(([key, val]) => ({
      key,
      label: val?.name || key,
    }));
  }, [officesMap]);

  const selectedManagerName = useMemo(() => {
    if (!form.managerKey) return "";
    const m = managersMap[form.managerKey];
    return m?.name || "";
  }, [form.managerKey, managersMap]);

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleChangeArea = (managerKeyNuevo) => {
    const desigRaw = managersMap[managerKeyNuevo]?.designation || "";
    const desig = desigRaw.replace(/^Encargad[ao]\s+de\s*/i, "").trim();
    setForm((prev) => ({
      ...prev,
      managerKey: managerKeyNuevo,
      designation: desig,
    }));
  };

  const handleDniChange = (value) => {
    const onlyDigits = String(value).replace(/[^\d]/g, "").slice(0, 8);
    setForm((prev) => ({ ...prev, dni: onlyDigits }));
  };

  const handleLeaveInput = (type, value) => {
    let v = String(value).replace(/[^\d]/g, "");
    if (v === "") {
      setForm((prev) => ({
        ...prev,
        leaves: { ...prev.leaves, [type]: "" },
      }));
      return;
    }
    let n = parseInt(v, 10);
    if (!Number.isFinite(n)) n = 0;
    if (n < 0) n = 0;
    if (n > 30) n = 30;
    setForm((prev) => ({
      ...prev,
      leaves: { ...prev.leaves, [type]: n },
    }));
  };

  const handleAddOffice = (officeKeyRaw) => {
    const v = String(officeKeyRaw || "").trim();
    if (!v) return;
    setForm((prev) => {
      const set = new Set(prev.allotted_office || []);
      set.add(v);
      return { ...prev, allotted_office: Array.from(set) };
    });
  };

  const handleRemoveOffice = (officeKey) => {
    setForm((prev) => ({
      ...prev,
      allotted_office: (prev.allotted_office || []).filter((x) => x !== officeKey),
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setError("");

    if (!form.dni || form.dni.length !== 8) return setError("El DNI debe tener 8 dígitos.");
    if (!form.nombres.trim()) return setError("Nombres es obligatorio.");
    if (!form.apePaterno.trim()) return setError("Apellido paterno es obligatorio.");
    if (!form.apeMaterno.trim()) return setError("Apellido materno es obligatorio.");
    if (!isEditMode && !form.email.trim()) return setError("El correo es obligatorio.");
    if (!isEditMode && !form.password.trim()) return setError("La contraseña es obligatoria.");
    if (!form.managerKey) return setError("Debes seleccionar un área (designación).");
    if (!form.designation) return setError("Debes seleccionar el área (designación).");
    if (!Array.isArray(form.allotted_office) || form.allotted_office.length === 0) {
      return setError("Debes seleccionar al menos una oficina.");
    }

    const Name = `${form.nombres.trim()} ${form.apePaterno.trim()} ${form.apeMaterno.trim()}`.replace(/\s+/g, " ");

    const op = Number.isFinite(parseInt(form.leaves.op, 10)) ? parseInt(form.leaves.op, 10) : 0;
    const va = Number.isFinite(parseInt(form.leaves.va, 10)) ? parseInt(form.leaves.va, 10) : 0;
    const sc = Number.isFinite(parseInt(form.leaves.sc, 10)) ? parseInt(form.leaves.sc, 10) : 0;
    const pe = Number.isFinite(parseInt(form.leaves.pe, 10)) ? parseInt(form.leaves.pe, 10) : 0;
    const pa = Number.isFinite(parseInt(form.leaves.pa, 10)) ? parseInt(form.leaves.pa, 10) : 0;

    try {
      const allottedOfficeCsv = (form.allotted_office || []).join(", ");
      await onConfirm?.({
        UUID: form.dni,
        Name,
        Email: form.email.trim(),
        Password: form.password,
        PhoneNumber: form.phone ? String(form.phone) : "",
        Address: form.address || "",
        manager: form.managerKey,
        managerName: managersMap[form.managerKey]?.name,
        designation: form.designation,
        allotted_office: allottedOfficeCsv,
        leaves: { op, va, sc, pe, pa },
        active: true,
      });
      onClose?.();
    } catch (e2) {
      setError(e2?.message || `No se pudo ${isEditMode ? "actualizar" : "crear"} el usuario.`);
    }
  };

  if (!open) return null;

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true">
      <div className="modal-card">
        <div className="modal-header">
          <h3>{isEditMode ? "Editar usuario" : "Agregar usuario"}</h3>
          <button className="icon-btn" onClick={onClose} aria-label="Cerrar" disabled={submitting}>
            ×
          </button>
        </div>

        <form className="modal-body" onSubmit={handleSubmit}>
          <div className="grid-2">
            <div className="form-row">
              <label>DNI</label>
              <input type="text" inputMode="numeric" placeholder="8 dígitos" value={form.dni} onChange={(e) => handleDniChange(e.target.value)} autoFocus />
            </div>

            <div className="form-row">
              <label>Teléfono</label>
              <input type="tel" placeholder="Ej.: 987654321" value={form.phone} onChange={(e) => handleChange("phone", e.target.value)} />
            </div>

            <div className="form-row">
              <label>Nombres</label>
              <input type="text" placeholder="Ej.: Jose" value={form.nombres} onChange={(e) => handleChange("nombres", e.target.value)} />
            </div>

            <div className="form-row">
              <label>Apellido paterno</label>
              <input type="text" placeholder="Ej.: Quispe" value={form.apePaterno} onChange={(e) => handleChange("apePaterno", e.target.value)} />
            </div>

            <div className="form-row">
              <label>Apellido materno</label>
              <input type="text" placeholder="Ej.: Condori" value={form.apeMaterno} onChange={(e) => handleChange("apeMaterno", e.target.value)} />
            </div>

            <div className="form-row">
              <label>Correo</label>
              <input type="email" placeholder="usuario@dominio.com" value={form.email} onChange={(e) => handleChange("email", e.target.value)} disabled={isEditMode} />
            </div>

            <div className="form-row">
              <label>Contraseña</label>
              <input type="password" placeholder={isEditMode ? "Sin cambio en edición" : "Mínimo 6 caracteres"} value={form.password} onChange={(e) => handleChange("password", e.target.value)} disabled={isEditMode} />
            </div>

            <div className="form-row" style={{ gridColumn: "1 / -1" }}>
              <label>Dirección</label>
              <input type="text" placeholder="Ej.: Cusco" value={form.address} onChange={(e) => handleChange("address", e.target.value)} />
            </div>
          </div>

          <div className="grid-2">
            <div className="form-row">
              <label>Designación (área)</label>
              <select value={form.managerKey} onChange={(e) => handleChangeArea(e.target.value)} disabled={loadingManagers || areaOptions.length === 0}>
                {areaOptions.length === 0 ? (
                  <option value="">(No hay áreas / managers)</option>
                ) : (
                  areaOptions.map((opt) => {
                    const cleanLabel = opt.designation.replace(/^Encargad[ao]\s+de\s*/i, "").trim();
                    return <option key={opt.key} value={opt.key}>{cleanLabel}</option>;
                  })
                )}
              </select>
            </div>

            <div className="form-row">
              <label>Encargado</label>
              <input type="text" value={selectedManagerName} readOnly />
            </div>
          </div>

          <div className="form-row">
            <label>Oficina</label>
            <div className="office-picker">
              <select
                value=""
                onChange={(e) => handleAddOffice(e.target.value)}
                disabled={loadingOffices || officeOptions.length === 0}
              >
                <option value="">Selecciona una oficina...</option>
                {officeOptions.length === 0 ? (
                  <option value="">(No hay oficinas)</option>
                ) : (
                  officeOptions.map((o) => (
                    <option key={o.key} value={o.key}>
                      {o.label}
                    </option>
                  ))
                )}
              </select>
            </div>
            <div className="office-chips">
              {(form.allotted_office || []).map((officeKey) => (
                <span className="office-chip" key={officeKey}>
                  {officesMap[officeKey]?.name || officeKey}
                  <button
                    type="button"
                    className="office-chip-remove"
                    onClick={() => handleRemoveOffice(officeKey)}
                    aria-label={`Quitar oficina ${officesMap[officeKey]?.name || officeKey}`}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
            <small className="subtext">Se guardará como texto separado por comas.</small>
          </div>

          <div className="leave-section">
            <label>Permisos disponibles</label>
            <div className="leave-row">
              {Object.keys(TYPE_LABELS).map((type) => (
                <div className="leave-item" key={type}>
                  <small className="subtext">{TYPE_LABELS[type]}</small>
                  <input type="number" min="0" max="30" step="1" className="leave-input" value={form.leaves[type] === "" ? "" : form.leaves[type]} onChange={(e) => handleLeaveInput(type, e.target.value)} />
                </div>
              ))}
            </div>
            <small className="subtext">Ingrese días (enteros) entre 0 y 30.</small>
          </div>

          {error && <div className="error">{error}</div>}
        </form>

        <div className="modal-actions">
          <button className="btn ghost" type="button" onClick={onClose} disabled={submitting}>Cancelar</button>
          <button className="btn primary" type="button" onClick={handleSubmit} disabled={submitting}>
            {submitting ? (isEditMode ? "Guardando..." : "Creando...") : (isEditMode ? "Guardar cambios" : "Crear usuario")}
          </button>
        </div>
      </div>
    </div>
  );
}
